<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const isRemovingFromSurvey = ref(false);
const isEnrollingInSurvey = ref(false);
const isSendingReviewReminder = ref(false);

const sender = computed(() => currentChat.value?.meta?.sender || {});

const hasLinkedClient = computed(() => {
  return Boolean(sender.value.client_id || sender.value.clientId);
});

const isSurveyProhibited = computed(() => {
  const surveyProhibited =
    sender.value.survey_prohibited ?? sender.value.surveyProhibited;
  return surveyProhibited === true;
});

const isWhatsappConversation = computed(() => {
  return currentChat.value?.meta?.channel === INBOX_TYPES.WHATSAPP;
});

const showSendReviewReminder = computed(
  () => store.getters['globalConfig/get']?.sendReviewReminderEnabled !== false
);

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  if (
    hasLinkedClient.value &&
    isWhatsappConversation.value &&
    showSendReviewReminder.value
  ) {
    items.push({
      icon: 'i-lucide-star',
      label: t('CONTACT_PANEL.SEND_REVIEW_REMINDER'),
      action: 'send_review_reminder',
      value: 'send_review_reminder',
      disabled: isSendingReviewReminder.value || isSurveyProhibited.value,
    });
  }

  if (hasLinkedClient.value && !isSurveyProhibited.value) {
    items.push({
      icon: 'i-lucide-clipboard-x',
      label: t('CONTACT_PANEL.REMOVE_FROM_SURVEY'),
      action: 'remove_from_survey',
      value: 'remove_from_survey',
      disabled: isRemovingFromSurvey.value,
    });
  }

  if (hasLinkedClient.value && isSurveyProhibited.value) {
    items.push({
      icon: 'i-lucide-clipboard-check',
      label: t('CONTACT_PANEL.ENROL_IN_SURVEY'),
      action: 'enrol_in_survey',
      value: 'enrol_in_survey',
      disabled: isEnrollingInSurvey.value,
    });
  }

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'remove_from_survey') {
    removeFromSurvey();
  } else if (action === 'enrol_in_survey') {
    enrolInSurvey();
  } else if (action === 'send_review_reminder') {
    sendReviewReminder();
  }
};

const sendReviewReminder = async () => {
  const conversationId = currentChat.value?.id;
  if (
    !conversationId ||
    !hasLinkedClient.value ||
    !isWhatsappConversation.value ||
    isSurveyProhibited.value ||
    isSendingReviewReminder.value
  ) {
    return;
  }

  isSendingReviewReminder.value = true;
  try {
    await ConversationApi.sendReviewReminder(conversationId);
    useAlert(t('CONTACT_PANEL.SEND_REVIEW_REMINDER_SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('CONTACT_PANEL.SEND_REVIEW_REMINDER_ERROR');
    useAlert(errorMessage, 'error');
  } finally {
    isSendingReviewReminder.value = false;
  }
};

const removeFromSurvey = async () => {
  const contactId = sender.value.id;
  if (
    !contactId ||
    !hasLinkedClient.value ||
    isSurveyProhibited.value ||
    isRemovingFromSurvey.value
  ) {
    return;
  }

  isRemovingFromSurvey.value = true;
  try {
    await store.dispatch('contacts/removeFromSurvey', contactId);
    useAlert(t('CONTACT_PANEL.REMOVE_FROM_SURVEY_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACT_PANEL.REMOVE_FROM_SURVEY_ERROR'));
  } finally {
    isRemovingFromSurvey.value = false;
  }
};

const enrolInSurvey = async () => {
  const contactId = sender.value.id;
  if (
    !contactId ||
    !hasLinkedClient.value ||
    !isSurveyProhibited.value ||
    isEnrollingInSurvey.value
  ) {
    return;
  }

  isEnrollingInSurvey.value = true;
  try {
    await store.dispatch('contacts/enrolInSurvey', contactId);
    useAlert(t('CONTACT_PANEL.ENROL_IN_SURVEY_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACT_PANEL.ENROL_IN_SURVEY_ERROR'));
  } finally {
    isEnrollingInSurvey.value = false;
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
